// RefillLines (truck stock, BL-203): a truck's next refill from the warehouse (TR-0419), for the office to
// check and send. Each line says why it's on it: below min tops up to max by itself; a tech's ask, with
// when he asked and what for; what the last restock came short. The office trims an amount, approves an
// ask, or removes a line (Undo puts it back); the warehouse's own stock is said beside it. Send to the
// warehouse says what's missing first (an ask to approve or remove, an amount that isn't a number), in ink
// after StateMark's red bar, the field's own line under it, and takes the focus there; sent, it says so and
// the lines stay to read. At our cost, for whoever sees our cost. The tech reads his own: each line and its
// amount, an ask he made waiting for the office. Built from LineTable, Field, Button, Link and Notice.
// Reads only --refill-*.
// New (Domain/Stock): LineTable draws its lines; what's new is a refill's rules (trim, approve an ask,
// remove with Undo, Send checking them first).
import { useId, useState, type ReactNode } from 'react';
import { money } from '../../format.ts';
import { Button } from '../Button/Button.tsx';
import { TextField } from '../Field/Field.tsx';
import { LineTable, type LineColumn, type LineRow } from '../LineTable/LineTable.tsx';
import { Link } from '../Link/Link.tsx';
import { Notice } from '../Notice/Notice.tsx';
import { StateMark } from '../StateMark/StateMark.tsx';
import { qtyWords, readQty } from '../stock-words.ts';
import './RefillLines.css';

export interface RefillLine {
  itemId: string;
  itemName: string;
  unit: string;
  /** below: under its min, tops up to max; ask: a tech asked for it; short: the last restock came short. */
  kind: 'below' | 'ask' | 'short';
  /** How many, as stock.levels writes it. */
  qty: string;
  /** Why it's on the refill, in words ("Sam’s ask, Mon 26 Oct, 10:44 a.m.: for J-24631"). */
  why: string;
  /** What the warehouse has of it: "240 ft"; with `warehouseLow`, under its own min. */
  warehouseHas?: string;
  warehouseLow?: boolean;
  /** One at our cost: only for whoever sees our cost. */
  eachCents?: number;
}
export interface RefillLinesProps {
  /** The refill: "TR-0419", and when it goes: "Tue 27 Oct, 7:15 a.m.". */
  refNo: string;
  at: string;
  /** Whose asks: "Sam". */
  first: string;
  lines: RefillLine[];
  /** One line above the lines that says what the restock on its way already brings. */
  context?: ReactNode;
  /** The tech's own: read only. */
  readOnly?: boolean;
  /** Where the work starts: asks approved, lines removed, amounts trimmed, Send tried, sent. */
  initial?: {
    approved?: string[];
    removed?: string[];
    qty?: Record<string, string>;
    tried?: boolean;
    sent?: boolean;
  };
  onSend?: (lines: { itemId: string; qty: string }[]) => void;
}

const has = (list: string[], id: string) => list.includes(id);

export function RefillLines({
  refNo,
  at,
  first,
  lines,
  context,
  readOnly,
  initial = {},
  onSend,
}: RefillLinesProps) {
  const uid = useId();
  const [approved, setApproved] = useState<string[]>(initial.approved ?? []);
  const [removed, setRemoved] = useState<string[]>(initial.removed ?? []);
  const [qty, setQty] = useState<Record<string, string>>(
    Object.fromEntries(lines.map((l) => [l.itemId, initial.qty?.[l.itemId] ?? l.qty])),
  );
  const [tried, setTried] = useState(!!initial.tried);
  const [sent, setSent] = useState(!!initial.sent);
  const cost = lines.some((l) => l.eachCents !== undefined);
  const live = lines.filter((l) => !has(removed, l.itemId));
  const toApprove = live.filter((l) => l.kind === 'ask' && !has(approved, l.itemId));
  const idOf = (item: string) => `${uid}-${item}`;

  const problems: { item: string; field?: string; msg: string; target: string }[] = [];
  for (const l of live) {
    if (l.kind === 'ask' && !has(approved, l.itemId))
      problems.push({
        item: l.itemId,
        msg: `Approve or remove ${first}’s ask for ${l.itemName} first`,
        target: `${idOf(l.itemId)}-ok`,
      });
    else if (readQty(qty[l.itemId] ?? '') === null || readQty(qty[l.itemId] ?? '') === 0)
      problems.push({
        item: l.itemId,
        field: 'Not a number more than 0',
        msg: `${l.itemName}: how many isn’t a number more than 0`,
        target: idOf(l.itemId),
      });
  }
  if (!live.length)
    problems.push({ item: '', msg: 'Nothing is left on the refill: Undo puts a line back', target: '' });
  const send = () => {
    setTried(true);
    const p0 = problems[0];
    if (p0) {
      const el = p0.target ? document.getElementById(p0.target) : null;
      // Approve's wrapper holds its button; an amount's holds its input
      el?.querySelector<HTMLElement>('button, input')?.focus();
      return;
    }
    setSent(true);
    onSend?.(live.map((l) => ({ itemId: l.itemId, qty: qty[l.itemId]! })));
  };
  const total = live.reduce((s, l) => s + (l.eachCents ?? 0) * (readQty(qty[l.itemId] ?? '') ?? 0), 0);

  const lock = readOnly || sent;
  const columns: LineColumn[] = [
    { id: 'item', label: 'Item' },
    { id: 'qty', label: 'How many', align: lock ? 'end' : 'start' },
    ...(lock ? [] : [{ id: 'act', label: <span className="fl-refill__sr">What to do</span> }]),
  ];
  const rows: LineRow[] = lines.map((l) => {
    const gone = has(removed, l.itemId);
    const ok = has(approved, l.itemId);
    const n = readQty(qty[l.itemId] ?? '');
    const where =
      readOnly || !l.warehouseHas
        ? ''
        : ` · the warehouse has ${l.warehouseHas}${l.warehouseLow ? ', under its own min' : ''}`;
    const err = tried && !gone ? problems.find((p) => p.item === l.itemId && p.field)?.field : undefined;
    return {
      id: l.itemId,
      tone: gone ? 'gone' : undefined,
      cells: {
        item: (
          <span className="fl-refill__item">
            <span className="fl-refill__name">
              {l.itemName}
              {l.kind === 'ask' && ok && !readOnly ? <span className="fl-refill__ok"> approved</span> : null}
            </span>
            <span className="fl-refill__why">
              {l.why}
              {where}
              {cost && !gone && n ? ` · ${money(Math.round(n * (l.eachCents ?? 0)))}` : ''}
            </span>
          </span>
        ),
        qty: lock ? (
          <span className="fl-refill__said">
            <strong>{qtyWords(qty[l.itemId]!, l.unit)}</strong>
            {readOnly && l.kind === 'ask' ? (
              <span className="fl-refill__wait">waiting for the office</span>
            ) : null}
            {gone ? <span className="fl-refill__muted">removed</span> : null}
          </span>
        ) : gone ? (
          { tone: 'none', value: 'Removed' }
        ) : (
          <span className="fl-refill__qty" id={idOf(l.itemId)}>
            <TextField
              label={`${l.itemName}: how many${l.unit === 'each' ? '' : ` (${l.unit})`}`}
              hideLabel
              inputMode="decimal"
              value={qty[l.itemId] ?? ''}
              onChange={(v) => setQty((q) => ({ ...q, [l.itemId]: v }))}
              error={err}
            />
            {l.unit === 'each' ? null : <span className="fl-refill__unit">{l.unit}</span>}
          </span>
        ),
        ...(lock
          ? {}
          : {
              act: gone ? (
                <span className="fl-refill__acts">
                  <Link onPress={() => setRemoved((r) => r.filter((x) => x !== l.itemId))}>Undo</Link>
                </span>
              ) : (
                <span className="fl-refill__acts">
                  {l.kind === 'ask' && !ok ? (
                    <span id={`${idOf(l.itemId)}-ok`} className="fl-refill__approve">
                      <Button size="sm" onPress={() => setApproved((a) => [...a, l.itemId])}>
                        Approve
                      </Button>
                    </span>
                  ) : null}
                  <Link onPress={() => setRemoved((r) => [...r, l.itemId])}>Remove</Link>
                </span>
              ),
            }),
      },
    };
  });
  return (
    <div className="fl-refill">
      {context ? <p className="fl-refill__context">{context}</p> : null}
      {sent ? (
        <Notice
          tone="done"
          takesFocus={!initial.sent}
          title={`Sent to the warehouse: ${refNo}, ${live.length} ${live.length === 1 ? 'line' : 'lines'} for ${at}.`}
        >
          <p>
            It’s on Picklists for the morning pick. {first} sees it as coming tomorrow, with the asks marked
            approved or not this time.
          </p>
        </Notice>
      ) : (
        <p className="fl-refill__muted">
          {readOnly
            ? `Coming ${at}, from the warehouse.`
            : `Picked for ${at}: below min tops up to max by itself; ${
                toApprove.length
                  ? `approve or remove ${first}’s ${toApprove.length === 1 ? 'ask' : `${toApprove.length} asks`}, and trim what you need.`
                  : 'trim or remove what you need.'
              }`}
        </p>
      )}
      <LineTable label={`${refNo}’s lines`} columns={columns} rows={rows} />
      {lock ? null : (
        <div className="fl-refill__foot">
          <Button variant="primary" onPress={send}>
            Send to the warehouse
          </Button>
          <span className="fl-refill__muted">
            {live.length} {live.length === 1 ? 'line' : 'lines'}
            {cost ? `, ${money(Math.round(total))} at cost` : ''}
          </span>
          {tried && problems[0] ? (
            <span className="fl-refill__problem" role="alert">
              <StateMark state="problem" />
              {problems[0].msg}.
            </span>
          ) : null}
        </div>
      )}
    </div>
  );
}
